import React, { FC, useCallback, useMemo, useState } from 'react';

import { Button, Checkbox, Modal, Select, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { ApiError } from 'client/api/error';
import { observeMcp, publishAliceAgentApp, updateAliceAgentApp } from 'client/api/services/aliceAgentApps';
import editStyles from 'client/blocks/AliceAgentAppPage/AliceAgentAppEditPage.module.scss';
import { AliceAgentAppLogoUploader } from 'client/blocks/AliceAgentAppPage/AliceAgentAppLogoUploader';
import { LocalizedString, LocalizedTextInputs } from 'client/blocks/AliceAgentAppPage/LocalizedTextInputs';
import { Field } from 'client/blocks/form/Field/Field';
import { AliceAgentAppDTO, McpAuthType, McpServerType } from 'client/model/aliceAgentApp';
import { getIsAdmin } from 'client/utils';
import { translateAppStoreError } from 'client/utils/errorMessage';
import * as formKeyset from 'i18n/AliceExternalAppForm';

import styles from './AliceExternalAppEditPage.module.scss';
import {
    emptyResource,
    emptyTool,
    externalServerKeyMap,
    isSupportedResourceMimeType,
    ResourceFormState,
    resourceFromManifest,
    ToolFormState,
    toolFromManifest,
} from './AliceExternalAppForm.types';
import { useAppForm } from './formHook';
import { buildManifest, buildObserveRequest, isVerifiedForCurrentForm, toFormState } from './manifest';
import { ResourceCard } from './ResourceCard';
import { ToolCard } from './ToolCard';

const t = i18n(formKeyset);

const SERVER_TYPES: { value: McpServerType; label: string }[] = [
    { value: 'http', label: 'HTTP' },
    { value: 'sse', label: 'SSE' },
    { value: 'streamable-http', label: 'Streamable HTTP' },
    { value: 'ws', label: 'WS' },
];

const AUTH_TYPES: { value: McpAuthType; labelKey: keyof typeof formKeyset.en; disabled?: boolean }[] = [
    { value: 'none', labelKey: 'field-mcp-auth-none' },
    { value: 'apiKey', labelKey: 'field-mcp-auth-api-key' },
];

interface Props {
    app: AliceAgentAppDTO;
    isDraft: boolean;
    onSaved: () => Promise<unknown> | void;
}

export const AliceExternalAppEditPage: FC<Props> = ({ app, isDraft, onSaved }) => {
    const { add: toasterAdd } = useToaster();

    const initial = useMemo(() => toFormState(app, isDraft), [app, isDraft]);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [mcpStats, setMcpStats] = useState<{ tools: number; resources: number } | null>(null);
    const [fillConfirmOpen, setFillConfirmOpen] = useState(false);
    const [pendingFillData, setPendingFillData] = useState<{
        tools: ToolFormState[];
        resources: ResourceFormState[];
    } | null>(null);

    const status = app.draft.status;
    const isProcessing = status === 'mcpProcessing';
    const isFailed = status === 'mcpFailed';
    const mcpLocked = isProcessing;
    const readOnly = !isDraft || isProcessing;
    // Gozora is an internal infrastructure detail — admins control it directly when debugging,
    // regular developers always run through the tunnel (default `true` for external apps).
    const isAdmin = getIsAdmin();

    const form = useAppForm({
        defaultValues: initial,
        onSubmit: async ({ value, formApi }) => {
            try {
                await updateAliceAgentApp(app.skillId, {
                    name: value.name,
                    manifest: buildManifest(value),
                    useZora: value.useZora,
                    chatTestingEnabled: value.chatTestingEnabled,
                    ...(value.logoId !== initial.logoId && value.logoId ? { logoId: value.logoId } : {}),
                });
                await onSaved();
                toasterAdd({ title: t('toast-saved'), theme: 'success', name: 'SAVE_ALICE_EXTERNAL_APP' });
                formApi.reset(value);
            } catch (err) {
                let toastMessage = t('toast-save-error');
                if (err instanceof ApiError && err.payload?.typedErrors?.length) {
                    const fieldErrors: Record<string, string> = {};
                    for (const apiErr of err.payload.typedErrors) {
                        if (!apiErr.key) continue;
                        const fieldKey = externalServerKeyMap[apiErr.key];
                        if (!fieldKey) continue;
                        fieldErrors[fieldKey] = translateAppStoreError(apiErr.type, apiErr.arg);
                    }
                    if (Object.keys(fieldErrors).length) {
                        formApi.setErrorMap({ onSubmit: { form: '', fields: fieldErrors } });
                    }
                    const first = err.payload.typedErrors[0];
                    toastMessage = translateAppStoreError(first.type, first.arg);
                } else if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
                    toastMessage = err.message;
                }
                toasterAdd({ title: toastMessage, theme: 'danger', name: 'SAVE_ALICE_EXTERNAL_APP' });
            }
        },
    });

    const { Field: FormField, Subscribe } = form;

    const handleLogoUploaded = useCallback(
        (image: { id: string; url: string }) => {
            form.setFieldValue('logoId', image.id);
            form.setFieldValue('logoUrl', image.url);
        },
        [form],
    );

    const handleLogoError = (message: string) => {
        toasterAdd({ title: message, theme: 'danger', name: 'UPLOAD_ALICE_EXTERNAL_APP_LOGO' });
    };

    const handlePublish = async () => {
        if (!isDraft || isPublishing || form.state.isDirty || isProcessing || isFailed) return;
        setIsPublishing(true);
        try {
            await publishAliceAgentApp(app.skillId);
            await onSaved();
            toasterAdd({ title: t('toast-published'), theme: 'success', name: 'PUBLISH_ALICE_EXTERNAL_APP' });
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('toast-publish-error');
            toasterAdd({ title: message, theme: 'danger', name: 'PUBLISH_ALICE_EXTERNAL_APP' });
        } finally {
            setIsPublishing(false);
        }
    };

    const handleCheckConnection = async () => {
        if (isChecking || mcpLocked) return;
        setIsChecking(true);
        try {
            const value = form.state.values;
            // The verify endpoint both probes the server AND stores a fingerprint on the draft —
            // tools/resources/publish unlock only after this succeeds. The plain observe-mcp is
            // kept for stateless previews but we don't use it here.
            const res = await observeMcp(app.skillId, buildObserveRequest(value));
            setMcpStats({ tools: res.stats.toolCount, resources: res.stats.resourceCount });
            await onSaved();
            toasterAdd({
                title: t('mcp-connection-success', {
                    tools: String(res.stats.toolCount),
                    resources: String(res.stats.resourceCount),
                }),
                theme: 'success',
                name: 'OBSERVE_MCP',
            });
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('toast-mcp-check-error');
            toasterAdd({ title: message, theme: 'danger', name: 'VERIFY_MCP' });
            setMcpStats(null);
        } finally {
            setIsChecking(false);
        }
    };

    const handleFillRequest = async () => {
        if (isChecking || mcpLocked) return;
        setIsChecking(true);
        try {
            const value = form.state.values;
            const res = await observeMcp(app.skillId, buildObserveRequest(value));
            setMcpStats({ tools: res.stats.toolCount, resources: res.stats.resourceCount });
            await onSaved();
            // Stage the result and ask for confirmation before overwriting the form. The user
            // may have manual edits they do not want to lose silently.
            setPendingFillData({
                tools: res.tools.map((preview) =>
                    toolFromManifest({
                        name: preview.name,
                        title: preview.title,
                        description: preview.description,
                        inputSchema: preview.inputSchema,
                        annotations: preview.annotations,
                        meta: preview.meta,
                    }),
                ),
                // observe-mcp already filters to HTML widget mime types; keep the guard for stale clients.
                resources: res.resources
                    .filter((preview) => isSupportedResourceMimeType(preview.mimeType))
                    .map((preview) =>
                        resourceFromManifest({
                            uri: preview.uri,
                            mimeType: preview.mimeType,
                            name: preview.name,
                            description: preview.description,
                            meta: preview.meta,
                        }),
                    ),
            });
            setFillConfirmOpen(true);
        } catch (err) {
            const message =
                err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
                    ? err.message
                    : t('toast-mcp-check-error');
            toasterAdd({ title: message, theme: 'danger', name: 'FILL_FROM_MCP' });
        } finally {
            setIsChecking(false);
        }
    };

    const applyFill = () => {
        if (!pendingFillData) return;
        form.setFieldValue('tools', pendingFillData.tools);
        form.setFieldValue('resources', pendingFillData.resources);
        setFillConfirmOpen(false);
        setPendingFillData(null);
    };

    const cancelFill = () => {
        setFillConfirmOpen(false);
        setPendingFillData(null);
    };

    return (
        <>
            <form
                className={editStyles.formContainer}
                id="basic"
                onSubmit={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                }}
            >
                <FormField name="name">
                    {(field) => (
                        <Field required fieldTitle={t('field-name')} error={field.state.meta.errors[0]}>
                            <TextInput
                                className={editStyles.formElement}
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                onBlur={field.handleBlur}
                                disabled={readOnly}
                                size="xl"
                                placeholder={t('field-name-placeholder')}
                                validationState={field.state.meta.errors[0] ? 'invalid' : undefined}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="logoId">
                    {(field) => (
                        <Field required fieldTitle={t('field-logo')} error={field.state.meta.errors[0]}>
                            <Subscribe selector={(s) => s.values.logoUrl}>
                                {(logoUrl) => (
                                    <AliceAgentAppLogoUploader
                                        skillId={app.skillId}
                                        logoUrl={logoUrl}
                                        disabled={readOnly}
                                        onUploaded={handleLogoUploaded}
                                        onError={handleLogoError}
                                    />
                                )}
                            </Subscribe>
                        </Field>
                    )}
                </FormField>

                <FormField name="title">
                    {(field) => (
                        <Field fieldTitle={t('field-title')} fieldDescription={t('field-title-description')}>
                            <LocalizedTextInputs
                                value={field.state.value as LocalizedString}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="shortDescription">
                    {(field) => (
                        <Field
                            fieldTitle={t('field-short-description')}
                            fieldDescription={t('field-short-description-description')}
                        >
                            <LocalizedTextInputs
                                value={field.state.value as LocalizedString}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="prompt">
                    {(field) => (
                        <Field fieldTitle={t('field-prompt')} fieldDescription={t('field-prompt-description')}>
                            <LocalizedTextInputs
                                value={field.state.value as LocalizedString}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                                multiline
                            />
                        </Field>
                    )}
                </FormField>
            </form>

            <section className={editStyles.formContainer} id="mcp">
                <h1>{t('side-menu-mcp-server')}</h1>
                <FormField name="mcp.endpoint">
                    {(field) => (
                        <Field required fieldTitle={t('field-mcp-endpoint')} error={field.state.meta.errors[0]}>
                            <TextInput
                                className={editStyles.formElement}
                                value={field.state.value}
                                onUpdate={field.handleChange}
                                disabled={readOnly}
                                size="xl"
                                placeholder={t('field-mcp-endpoint-placeholder')}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="mcp.serverType">
                    {(field) => (
                        <Field fieldTitle={t('field-mcp-server-type')}>
                            <Select
                                className={editStyles.formElement}
                                value={[field.state.value]}
                                onUpdate={(v) => field.handleChange(v[0] as McpServerType)}
                                disabled={readOnly}
                                size="xl"
                                options={SERVER_TYPES.map((s) => ({ value: s.value, content: s.label }))}
                            />
                        </Field>
                    )}
                </FormField>

                <FormField name="mcp.authType">
                    {(field) => (
                        <Field fieldTitle={t('field-mcp-auth-type')}>
                            <Select
                                className={editStyles.formElement}
                                value={[field.state.value]}
                                onUpdate={(v) => field.handleChange(v[0] as McpAuthType)}
                                disabled={readOnly}
                                size="xl"
                                options={AUTH_TYPES.map((a) => ({
                                    value: a.value,
                                    content: t(a.labelKey),
                                    disabled: a.disabled,
                                }))}
                            />
                        </Field>
                    )}
                </FormField>

                <Subscribe selector={(s) => s.values.mcp.authType}>
                    {(authType) =>
                        authType === 'apiKey' ? (
                            <FormField name="mcp.apiKey">
                                {(field) => (
                                    <Field fieldTitle={t('field-mcp-api-key')}>
                                        <TextInput
                                            className={editStyles.formElement}
                                            value={field.state.value}
                                            onUpdate={field.handleChange}
                                            disabled={readOnly}
                                            size="xl"
                                            placeholder={t('field-mcp-api-key-placeholder')}
                                            type="password"
                                        />
                                    </Field>
                                )}
                            </FormField>
                        ) : null
                    }
                </Subscribe>

                {isAdmin && (
                    <FormField name="useZora">
                        {(field) => (
                            <Field
                                fieldTitle={t('field-mcp-use-zora')}
                                fieldDescription={t('field-mcp-use-zora-description')}
                            >
                                <Checkbox
                                    className={editStyles.formElement}
                                    checked={field.state.value}
                                    onChange={(e) => field.handleChange(e.target.checked)}
                                    disabled={readOnly}
                                    size="l"
                                >
                                    {t('field-mcp-use-zora-label')}
                                </Checkbox>
                            </Field>
                        )}
                    </FormField>
                )}

                <FormField name="isAuthorizationRequired">
                    {(field) => (
                        <Field
                            fieldTitle={t('field-auth-required')}
                            fieldDescription={t('field-auth-required-description')}
                        >
                            <Checkbox
                                className={editStyles.formElement}
                                checked={field.state.value}
                                onChange={(e) => field.handleChange(e.target.checked)}
                                disabled={readOnly}
                                size="l"
                            >
                                {t('field-auth-required-label')}
                            </Checkbox>
                        </Field>
                    )}
                </FormField>

                <Subscribe selector={(s) => s.values}>
                    {(values) => {
                        const canCheck =
                            Boolean(values.mcp.endpoint) &&
                            (values.mcp.authType !== 'apiKey' || Boolean(values.mcp.apiKey));
                        const verified = isVerifiedForCurrentForm(app, values);

                        return (
                            <div className={styles.mcpStatus}>
                                <Button
                                    view="outlined-action"
                                    onClick={handleCheckConnection}
                                    disabled={!canCheck || isChecking || readOnly}
                                    loading={isChecking}
                                >
                                    {t('action-check-connection')}
                                </Button>
                                <div
                                    className={clx(
                                        styles.mcpStatusLabel,
                                        verified ? styles.mcpStatusLabel_verified : styles.mcpStatusLabel_notVerified,
                                    )}
                                >
                                    {verified ? t('mcp-verified') : t('mcp-not-verified')}
                                </div>
                                {verified && mcpStats && (
                                    <div className={styles.mcpStatusStats}>
                                        {t('mcp-connection-success', {
                                            tools: String(mcpStats.tools),
                                            resources: String(mcpStats.resources),
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    }}
                </Subscribe>
            </section>

            <Subscribe selector={(s) => s.values}>
                {(values) => {
                    const verified = isVerifiedForCurrentForm(app, values);
                    const sectionDisabled = readOnly || !verified;
                    const canCheck =
                        Boolean(values.mcp.endpoint) &&
                        (values.mcp.authType !== 'apiKey' || Boolean(values.mcp.apiKey));
                    return (
                        <section className={`${editStyles.formContainer} ${styles.narrowSection}`} id="tools-resources">
                            <h1>{t('section-mcp-tools-resources-title')}</h1>
                            {!verified && <p className={styles.sectionHint}>{t('mcp-locked-until-check')}</p>}

                            <div className={styles.fillFromMcp}>
                                <p className={styles.fillFromMcpHint}>{t('fill-from-mcp-description')}</p>
                                <Button
                                    view="outlined"
                                    onClick={handleFillRequest}
                                    disabled={!canCheck || isChecking || readOnly || !verified}
                                >
                                    {t('action-fill-from-mcp')}
                                </Button>
                            </div>

                            <h2 className={styles.subsectionHeading}>{t('subsection-tools-title')}</h2>
                            <FormField name="tools" mode="array">
                                {(field) => (
                                    <>
                                        {field.state.value.length === 0 && (
                                            <p className={styles.sectionHint}>{t('section-tools-empty')}</p>
                                        )}
                                        {field.state.value.map((tool, i) => (
                                            <ToolCard
                                                key={tool.id}
                                                form={form}
                                                index={i}
                                                disabled={sectionDisabled}
                                                onRemove={() => field.removeValue(i)}
                                            />
                                        ))}
                                        <Button
                                            className={editStyles.formButton}
                                            disabled={sectionDisabled}
                                            onClick={() => field.pushValue(emptyTool())}
                                        >
                                            {t('action-add-tool')}
                                        </Button>
                                    </>
                                )}
                            </FormField>

                            <h2 className={styles.subsectionHeading_pulled}>{t('subsection-resources-title')}</h2>
                            <FormField name="resources" mode="array">
                                {(field) => (
                                    <>
                                        {field.state.value.length === 0 && (
                                            <p className={styles.sectionHint}>{t('section-resources-empty')}</p>
                                        )}
                                        {field.state.value.map((resource, i) => (
                                            <ResourceCard
                                                key={resource.id}
                                                form={form}
                                                index={i}
                                                disabled={sectionDisabled}
                                                onRemove={() => field.removeValue(i)}
                                            />
                                        ))}
                                        <Button
                                            className={editStyles.formButton}
                                            disabled={sectionDisabled}
                                            onClick={() => field.pushValue(emptyResource())}
                                        >
                                            {t('action-add-resource')}
                                        </Button>
                                    </>
                                )}
                            </FormField>
                        </section>
                    );
                }}
            </Subscribe>

            {isDraft && (
                <>
                    <Subscribe selector={(s) => s.values}>
                        {(values) => {
                            // chatTesting is allowed only when the draft is ready end-to-end:
                            // status back to inDevelopment, logo + endpoint set, MCP verified,
                            // every resource already processed (has internalUri). Otherwise
                            // Kronstadt's testing cache would hit a partial manifest.
                            const allResourcesProcessed =
                                values.resources.length === 0 || values.resources.every((r) => Boolean(r.internalUri));
                            const verified = isVerifiedForCurrentForm(app, values);
                            const chatTestingReady =
                                status === 'inDevelopment' &&
                                Boolean(values.logoId) &&
                                Boolean(values.mcp.endpoint) &&
                                verified &&
                                allResourcesProcessed;
                            return (
                                <div className={editStyles.formContainer}>
                                    <FormField name="chatTestingEnabled">
                                        {(field) => (
                                            <Field
                                                fieldTitle={t('field-chat-testing')}
                                                fieldDescription={
                                                    !chatTestingReady
                                                        ? t('field-chat-testing-disabled-processing')
                                                        : undefined
                                                }
                                            >
                                                <Checkbox
                                                    className={editStyles.formElement}
                                                    checked={field.state.value && chatTestingReady}
                                                    onChange={(e) => field.handleChange(e.target.checked)}
                                                    disabled={readOnly || !chatTestingReady}
                                                    size="l"
                                                >
                                                    {t('field-chat-testing-label')}
                                                </Checkbox>
                                            </Field>
                                        )}
                                    </FormField>
                                </div>
                            );
                        }}
                    </Subscribe>

                    <Subscribe selector={(s) => s}>
                        {(s) => {
                            const verified = isVerifiedForCurrentForm(app, s.values);
                            return (
                                <div className={editStyles.footer}>
                                    <Button
                                        view="action"
                                        size="l"
                                        onClick={() => form.handleSubmit()}
                                        disabled={
                                            !s.isDirty || !s.canSubmit || s.isSubmitting || isPublishing || isProcessing
                                        }
                                        loading={s.isSubmitting}
                                    >
                                        {t('action-save')}
                                    </Button>
                                    <Button
                                        view="outlined-action"
                                        size="l"
                                        onClick={handlePublish}
                                        disabled={
                                            s.isDirty ||
                                            s.isSubmitting ||
                                            isPublishing ||
                                            isProcessing ||
                                            isFailed ||
                                            !verified
                                        }
                                        loading={isPublishing}
                                    >
                                        {t('action-publish')}
                                    </Button>
                                </div>
                            );
                        }}
                    </Subscribe>
                </>
            )}

            <Modal open={fillConfirmOpen} onClose={cancelFill}>
                <div className={styles.fillModal}>
                    <h3>{t('mcp-fill-warning-title')}</h3>
                    <p>{t('mcp-fill-warning-body')}</p>
                    <div className={styles.fillModalActions}>
                        <Button view="flat" onClick={cancelFill}>
                            {t('mcp-fill-cancel')}
                        </Button>
                        <Button view="action" onClick={applyFill}>
                            {t('mcp-fill-confirm')}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
